import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseSecurityGuidelines } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage security guidelines versions
 * Stores and tracks different versions of security guidelines from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useSecurityGuidelinesVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for security guidelines version management
  const securityGuidelinesConfig = {
    // Determine if a message contains security guidelines
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('Security') ||
             content.includes('Authentication') ||
             content.includes('Authorization') ||
             content.includes('Vulnerability') ||
             content.includes('OWASP') ||
             content.includes('Testing Strategy');
    },

    // Parse security guidelines sections from content
    // Uses parseSecurityGuidelines which treats h2 as categories and h3 as sections
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseSecurityGuidelines(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(securityGuidelinesConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to security guidelines-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useSecurityGuidelinesVersions;
